import { FC, useMemo, useCallback } from 'react'
import { useLoadAllItemsQuery, useQuery } from '@sequencehq/api'
import {
  Text,
  Badge,
  Button,
  EmptyState,
  GreyGrey80,
  IconPlus,
  IconReceiptTax,
  IndigoIndigo60,
  Link,
  Primary14SemiBold,
  SequenceMenu,
  SequenceMenuItemProps,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMarkTaxCategoryAsDefault } from 'SettingsV2/components/tax-rates/hooks/useMarkTaxCategoryAsDefault'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import { NumeralTaxCategoryMapping } from '../taxRates.types'
import { numeralIntegrationConfig } from 'Integrations/integrationsConfig/numeral/numeral.integration.config'
import { SmallIcon } from 'Integrations/index'
import { useCreateTaxCategory } from 'SettingsV2/components/tax-rates/hooks/useCreateTaxCategory'
import { useCreateNumeralMapping } from './hooks/useCreateNumeralMapping'
import { useDeleteNumeralMapping } from './hooks/useDeleteNumeralMapping'
import { useUpdateNumeralMapping } from './hooks/useUpdateNumeralMapping'
import { useUpdateTaxCategory } from '../hooks/useUpdateTaxCategory'
import { TaxCategoryModal } from 'SettingsV2/components/tax-rates/modals/TaxCategoryModal'
import EditTaxCategoryNameModal from './modals/EditTaxCategoryNameModal'
import EditNumeralMappingModal from './modals/EditNumeralMappingModal'
import AddNumeralTaxCategoryModal from './modals/AddNumeralTaxCategoryModal'

export const NumeralSalesTaxSettings: FC = () => {
  const {
    data: taxCategoriesData,
    isError,
    isLoading
  } = useLoadAllItemsQuery(dashboardv99990101Client.getTaxCategories)

  const mappingsResult = useQuery(
    dashboardv99990101Client.getAllTaxCategoriesWithNumeralMappings,
    undefined,
    {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const allExistingTaxCategories = useMemo(
    () => taxCategoriesData?.pages.flatMap(page => page.items) ?? [],
    [taxCategoriesData?.pages]
  )

  const taxCategoriesWithNumeralMappings: NumeralTaxCategoryMapping[] =
    useMemo(() => {
      return allExistingTaxCategories.map(taxCategory => {
        const numeralMapping = mappingsResult.data?.items?.find(
          ({ taxCategoryId }) => taxCategoryId === taxCategory.id
        )

        return {
          taxCategoryId: taxCategory.id,
          taxCategoryName: taxCategory.name,
          isDefault: taxCategory.isDefault,
          numeralProductCategory: numeralMapping?.numeralProductCategory ?? '',
          numeralProductCategoryTitle:
            numeralMapping?.numeralProductCategoryTitle ?? ''
        }
      })
    }, [allExistingTaxCategories, mappingsResult.data?.items])

  const createTaxCategory = useCreateTaxCategory()
  const createNumeralMapping = useCreateNumeralMapping()
  const updateNumeralMapping = useUpdateNumeralMapping()
  const deleteNumeralMapping = useDeleteNumeralMapping()
  const markTaxCategoryAsDefault = useMarkTaxCategoryAsDefault()
  const updateTaxCategory = useUpdateTaxCategory()

  const handleUpdateTaxCategoryName = useCallback(
    async (taxCategoryId: string, name: string) => {
      const taxCategory = allExistingTaxCategories.find(
        tc => tc.id === taxCategoryId
      )
      if (taxCategory) {
        await updateTaxCategory(taxCategory, {
          name,
          description: taxCategory.description
        })
      }
    },
    [allExistingTaxCategories, updateTaxCategory]
  )

  return (
    <SettingsPageLayout
      header="Sales tax"
      subheader="Manage tax categories and their Numeral mappings."
      isError={isError || mappingsResult.isError}
    >
      <SettingsGroup
        isLoading={isLoading || mappingsResult.isLoading}
        id="tax-categories"
        headers={[
          { label: 'Sequence category', width: '300px' },
          { label: 'Numeral category', width: 'calc(100% - 304px)' }
        ]}
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              as="h3"
              id="tax-categories"
              {...Primary14SemiBold}
              color={GreyGrey80}
            >
              Tax categories
            </Text>

            <TaxCategoryModal
              onSubmit={createTaxCategory}
              trigger={
                <Button
                  variant="ghost"
                  leadingIcon={<IconPlus color="inherit" />}
                >
                  New category
                </Button>
              }
            />
          </Flex>
        }
      >
        {taxCategoriesWithNumeralMappings.length === 0 &&
          !isLoading &&
          !mappingsResult.isLoading && (
            <EmptyState
              title="No tax categories"
              icon={IconReceiptTax}
              iconSize="sm"
              body={
                <>
                  You have not added any tax categories yet. Add a tax category,{' '}
                  <Link
                    _hover={{ textDecoration: 'none' }}
                    to="https://docs.sequencehq.com/connectors/numeral"
                    color={IndigoIndigo60}
                    target="_blank"
                  >
                    learn more
                  </Link>
                  .
                </>
              }
            />
          )}

        {taxCategoriesWithNumeralMappings.map(mapping => (
          <SettingsFormControl
            key={mapping.taxCategoryId}
            htmlFor={`tax-category-${mapping.taxCategoryId}`}
          >
            <Flex width="100%" gap="4px" height="100%" alignItems="center">
              <Flex
                alignItems="center"
                gap="8px"
                minWidth="300px"
                maxWidth="300px"
              >
                <SettingsFormControlIcon>
                  <IconReceiptTax />
                </SettingsFormControlIcon>
                <Text fontWeight="medium" isTruncated>
                  {mapping.taxCategoryName}
                </Text>
                {mapping.isDefault && (
                  <Badge sentiment="neutral" size="sm">
                    Default
                  </Badge>
                )}
              </Flex>

              <Flex
                alignItems="center"
                gap="8px"
                minWidth="0"
                justifyContent="space-between"
                flex="1"
              >
                <Flex alignItems="center" gap="8px" flex="1" minWidth="0">
                  {mapping.numeralProductCategory ? (
                    <Badge sentiment="success" size="sm" truncate={true}>
                      {mapping.numeralProductCategoryTitle}
                    </Badge>
                  ) : (
                    <AddNumeralTaxCategoryModal
                      taxCategoryName={mapping.taxCategoryName}
                      onAddTaxCategory={async ({
                        numeralTaxCategory
                      }: {
                        numeralTaxCategory: string
                      }) => {
                        await createNumeralMapping({
                          taxCategoryId: mapping.taxCategoryId,
                          numeralProductCategory: numeralTaxCategory,
                          numeralProductCategoryTitle: numeralTaxCategory,
                          isDefault: mapping.isDefault
                        })
                      }}
                    />
                  )}
                </Flex>

                <SequenceMenu
                  variant="ghost"
                  buttonSize="small"
                  data-testid={`kebab.${mapping.taxCategoryName}`}
                  items={[
                    (props: Partial<SequenceMenuItemProps>) => (
                      <EditTaxCategoryNameModal
                        key="edit-name"
                        initialName={mapping.taxCategoryName}
                        onSubmit={async ({ name }: { name: string }) => {
                          await handleUpdateTaxCategoryName(
                            mapping.taxCategoryId,
                            name
                          )
                          return props?.setIsOpen?.(false)
                        }}
                        trigger={
                          <SequenceMenu.Item
                            data-testid={`editName.${mapping.taxCategoryName}`}
                            key="edit-name"
                            uuid="edit-name"
                            label="Edit name"
                          />
                        }
                      />
                    ),
                    ...(mapping.numeralProductCategory
                      ? [
                          (props: Partial<SequenceMenuItemProps>) => (
                            <EditNumeralMappingModal
                              taxCategoryName={mapping.taxCategoryName}
                              key="edit-mapping"
                              currentNumeralCategory={
                                mapping.numeralProductCategory
                              }
                              currentNumeralCategoryTitle={
                                mapping.numeralProductCategoryTitle
                              }
                              onUpdateMapping={async ({
                                numeralTaxCategory
                              }) => {
                                await updateNumeralMapping({
                                  taxCategoryId: mapping.taxCategoryId,
                                  numeralProductCategory: numeralTaxCategory,
                                  numeralProductCategoryTitle:
                                    numeralTaxCategory,
                                  isDefault: mapping.isDefault
                                })
                                return props?.setIsOpen?.(false)
                              }}
                              trigger={
                                <SequenceMenu.Item
                                  data-testid={`editMapping.${mapping.taxCategoryName}`}
                                  key="edit-mapping"
                                  uuid="edit-mapping"
                                  label="Edit mapping"
                                />
                              }
                            />
                          ),
                          (props: Partial<SequenceMenuItemProps>) => (
                            <SequenceMenu.Item
                              data-testid={`removeMapping.${mapping.taxCategoryName}`}
                              key="remove-mapping"
                              uuid="remove-mapping"
                              label="Remove mapping"
                              style={{ color: 'red' }}
                              onClick={() => {
                                void deleteNumeralMapping(mapping.taxCategoryId)
                                return props?.setIsOpen?.(false)
                              }}
                            />
                          )
                        ]
                      : []),
                    ...(!mapping.isDefault
                      ? [
                          (props: Partial<SequenceMenuItemProps>) => (
                            <SequenceMenu.Item
                              data-testid={`markAsDefault.${mapping.taxCategoryName}`}
                              key="mark-as-default"
                              uuid="mark-as-default"
                              label="Mark as default"
                              onClick={() => {
                                void markTaxCategoryAsDefault(
                                  mapping.taxCategoryId
                                )
                                return props?.setIsOpen?.(false)
                              }}
                            />
                          )
                        ]
                      : [])
                  ]}
                />
              </Flex>
            </Flex>
          </SettingsFormControl>
        ))}
      </SettingsGroup>

      <SettingsGroup title="Tax rates">
        <EmptyState
          title="Managed via Numeral"
          icon={NumeralLogo}
          iconSize="sm"
          body={
            <>
              Tax rates are managed by Numeral,{' '}
              <Link
                _hover={{ textDecoration: 'none' }}
                to="https://docs.sequencehq.com/connectors/numeral"
                color={IndigoIndigo60}
                target="_blank"
              >
                learn more
              </Link>
              .
            </>
          }
        />
      </SettingsGroup>
    </SettingsPageLayout>
  )
}

const NumeralLogo = () => {
  return <SmallIcon url={numeralIntegrationConfig.logoUrl} />
}
